import { AsyncButton } from "aic";
import { ChatLeftDots } from "react-bootstrap-icons";
import { CommentViewContext } from "./interface";
import { useComments } from "./useComments";

interface CreateCommentButtonProps {
  id?: string;
  activeContext: CommentViewContext;
  parentId?: string;
  relatedTo?: string;
  reply?: boolean;
  disabled?: boolean;
  label?: string;
}

/**
 * Button to create a new comment or reply to an existing comment.
 *
 * @param props - Destructured props object
 * @param props.id - Optional element id
 * @param props.activeContext - Active comment context
 * @param props.parentId - Optional parent comment id
 * @param props.relatedTo - Optional related entity id
 * @param props.reply - When true render as reply button
 * @param props.disabled - When true disable the button
 * @param props.label - Button label
 */
export const CreateCommentButton = ({
  id = "create-comment-button",
  activeContext,
  parentId,
  relatedTo,
  reply = false,
  disabled = false,
  label = reply ? "Reply" : "Create comment",
}: CreateCommentButtonProps): React.ReactElement => {
  // Get contexts
  const { createComment } = useComments(activeContext);

  /**
   * Handle click event to create a new comment.
   *
   * @param e - Mouse event
   */
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    /* istanbul ignore else */
    if (!disabled) createComment(parentId, relatedTo);
  };

  // Component render
  return (
    <AsyncButton
      id={id}
      title={label}
      aria-label={label}
      onClick={handleClick}
      size="sm"
      disabled={disabled}
    >
      {label === "Create comment" && <ChatLeftDots className="me-1" />}
      {label}
    </AsyncButton>
  );
};

CreateCommentButton.displayName = "CreateCommentButton";
